import { CondicoesInput, EsquemaCalendarioVacinalQuery, RegistrosDoseQuery } from 'graphql/types.generated'
import _ from 'lodash'
import groupBy from 'lodash/groupBy'
import { DoseItemProps } from 'view/atendimentos/detail/components/vacinacao/DoseItem'
import { ImunobiologicoRowProps } from 'view/atendimentos/detail/components/vacinacao/ImunobiologicoRow'
import {
  InfoCardModalVacinacao,
  RegistrosAplicadosDto,
} from 'view/atendimentos/detail/vacinacao/VacinacaoCalendarioView'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import {
  CondicoesFormModel,
  DoseItemModel,
  RegistroVacinacaoFormModel,
  StatusDoseEnum,
  TipoRegistroDoseEnum,
  VacinasAplicadas,
} from '../model'
import { RegistroDoseProps } from '../RegistroDose'
import { VacinacaoModalItem } from '../VacinacaoModal'
import { getStatusDoseVacinacao } from '../vacinacaoUtils'

interface DoseItem {
  model: DoseItemModel
}

interface CalendarioVacinalProps {
  abaCalendario: ImunobiologicoRowProps[]
  abaOutros: ImunobiologicoRowProps[]
}

export const convertCondicoesToInput = (form: CondicoesFormModel): CondicoesInput =>
  form && {
    isGestante: form.gestante,
    isPuerpera: form.puerpera,
    isViajante: form.viajante,
    isComunicanteHanseniase: form.comunicanteHanseniase,
  }

export const convertEsquemaCalendarioVacinalQueryToProps = (
  data: EsquemaCalendarioVacinalQuery,
  cidadao: CidadaoAtendimento,
  dataAtendimento: Instant,
  prontuarioId: string,
  loadRegistrosAplicados?: VacinasAplicadas[]
): CalendarioVacinalProps => {
  const loadRegistrosCalendario: Map<string, RegistrosAplicadosDto> = new Map()

  const loadRegistrosOutrosImunos: RegistrosAplicadosDto[] = []

  const calendario = groupBy(data.esquemaCalendarioVacinal, 'imunobiologico.id')

  if (loadRegistrosAplicados) {
    loadRegistrosAplicados?.forEach((value: RegistrosAplicadosDto) => {
      if (calendario[value.imunobiologicoId]?.find((item) => item.dose.id === value.doseId)) {
        loadRegistrosCalendario.set(value.imunobiologicoId + value.doseId, value)
      } else {
        loadRegistrosOutrosImunos.push(value)
      }
    })
  }

  const dosesCalendarioProps = calendarioDosesToProps(
    loadRegistrosCalendario,
    data,
    cidadao,
    dataAtendimento,
    prontuarioId
  )
  const doseOutrosProps = outrasDosesToProps(loadRegistrosOutrosImunos)
  return { abaCalendario: dosesCalendarioProps, abaOutros: doseOutrosProps }
}

const calendarioDosesToProps = (
  listToTransform: Map<string, RegistrosAplicadosDto>,
  data: EsquemaCalendarioVacinalQuery,
  cidadao: CidadaoAtendimento,
  dataAtendimento: Instant,
  prontuarioId: string
): ImunobiologicoRowProps[] => {
  return _.chain(data.esquemaCalendarioVacinal)
    .groupBy('imunobiologico.id')
    .map((value, key) => {
      let status: StatusDoseEnum
      let dataAprazamento: string
      const doseProp = value.map<DoseItem>((doseItem) => {
        if (listToTransform.get(key + doseItem.dose.id)) {
          const dose = listToTransform.get(key + doseItem.dose.id)
          status = dose?.dataAprazamento ? StatusDoseEnum.DISPONIVEL : StatusDoseEnum.APLICADA
          dataAprazamento = dose?.dataAprazamento
        } else {
          status = getStatusDoseVacinacao(
            cidadao.dataNascimento,
            dataAtendimento,
            doseItem.faixaEtaria.diasInicio,
            doseItem.faixaEtaria.diasFim
          )
          dataAprazamento = null
        }
        return {
          model: {
            calendarioId: doseItem.id,
            imunobiologicoId: key,
            doseId: doseItem.dose.id,
            nome: doseItem.dose.nomeApresentacaoDose,
            faixaEtaria: doseItem.idadeRecomendada,
            status: status,
            mostrarNoCalendarioDoGrupo: doseItem.mostrarNoCalendarioDoGrupo,
            dataAprazamento: dataAprazamento,
            nomeImuno: doseItem.descricaoImunobiologico,
            prontuarioId: prontuarioId,
            nomeCompletoImuno: doseItem.imunobiologico.nome,
            siglaImuno: doseItem.imunobiologico.sigla,
          },
        }
      })
      return {
        model: {
          imunobiologicoId: key,
          nome: value[0].descricaoImunobiologico,
          doses: doseProp,
          ordem: value[0].ordemImuno,
        },
      } as ImunobiologicoRowProps
    })
    .sortBy('model.ordem')
    .value()
}

const outrasDosesToProps = (listToTransform: RegistrosAplicadosDto[]): ImunobiologicoRowProps[] => {
  return _.chain(listToTransform)
    .groupBy('imunobiologicoId')
    .map((value) => {
      const doseProp = value.map<DoseItem>((doseItem) => {
        return {
          model: {
            imunobiologicoId: doseItem.imunobiologicoId,
            doseId: doseItem.doseId,
            nome: doseItem.doseNomeApresentacao,
            status: doseItem?.dataAprazamento ? StatusDoseEnum.DISPONIVEL : StatusDoseEnum.APLICADA,
            mostrarNoCalendarioDoGrupo: true,
            dataAprazamento: doseItem?.dataAprazamento,
            nomeImuno: doseItem.imunobiologicoNome,
          },
        } as DoseItemProps
      })
      return {
        model: {
          imunobiologicoId: value[0].imunobiologicoId,
          nome: value[0].imunobiologicoNome,
          doses: doseProp,
        },
      } as ImunobiologicoRowProps
    })
    .value()
}

export const updateDosesProps = (
  estadoInicial: ImunobiologicoRowProps[],
  registrosVacinacao: RegistroVacinacaoFormModel[]
): ImunobiologicoRowProps[] => {
  registrosVacinacao.forEach((item) => {
    let row = estadoInicial.find((element) => element.model.imunobiologicoId === item.imunobiologico)
    let dose = row?.model.doses.find((dose) => dose.model.doseId === item.doseImunobiologico)
    if (row && dose) {
      if (item.tipoRegistroVacinacao === TipoRegistroDoseEnum.APLICACAO) {
        dose.model.status = StatusDoseEnum.APLICADA
      } else if (item.tipoRegistroVacinacao === TipoRegistroDoseEnum.APRAZAMENTO) {
        dose.model.dataAprazamento = item.dataAprazamento
      } else if (item.tipoRegistroVacinacao === TipoRegistroDoseEnum.REGISTRO_ANTERIOR) {
        dose.model.status = StatusDoseEnum.APLICADA
      }
    }
  })
  return estadoInicial
}

export const createModalProps = (
  infoCardModal: InfoCardModalVacinacao,
  vacinacaoModalInfo: RegistrosDoseQuery,
  registrosModificados: RegistroVacinacaoFormModel[],
  nomeProfissional: string,
  cboProfissional: string,
  unidadeSaudeProfissional: string
): VacinacaoModalItem => {
  const vacinacaoItem: VacinacaoModalItem = {
    faixaEtaria: infoCardModal?.faixaEtaria,
    idImunobiologico: infoCardModal?.imunobiologicoId,
    nomeImunobiologico: infoCardModal?.imunobiologicoNome,
    idDose: infoCardModal?.doseId,
    nomeDose: infoCardModal?.doseNome,
    siglaImunobiologico: infoCardModal?.imunobiologicoSigla,
    registros: [],
  } as VacinacaoModalItem

  registrosModificados?.map((registroModificado) => {
    if (
      infoCardModal?.imunobiologicoId === registroModificado?.imunobiologico &&
      infoCardModal?.doseId === registroModificado?.doseImunobiologico
    ) {
      vacinacaoItem.registros.push({
        cbo: cboProfissional,
        dataAplicacao: registroModificado?.dataAplicacao,
        dataAprazamento: registroModificado?.dataAprazamento,
        dataRegistro: registroModificado?.dataRegistro,
        localDaAplicacao: registroModificado?.localAplicacaoVacinacao?.nome,
        estrategia: registroModificado?.estrategiaVacinacao?.nome,
        nomeProfissional: nomeProfissional,
        lote: registroModificado?.lote || registroModificado?.loteImunobiologico?.lote,
        observacoes: registroModificado?.observacoes,
        viaDeAdministracao: registroModificado?.viaAdministracao?.nome,
        nomeFabricante:
          registroModificado?.loteFabricante ||
          registroModificado?.loteImunobiologico?.fabricante.nome ||
          registroModificado?.fabricante?.nome,
        isGestante: false,
        isPuerpera: false,
        isViajante: false,
        dataValidadeLote: registroModificado?.loteDataValidade || registroModificado?.loteImunobiologico?.validade,
        unidadeSaude: unidadeSaudeProfissional,
      } as RegistroDoseProps)
    }
  })

  vacinacaoModalInfo?.registrosDose.map((registro) => {
    vacinacaoItem.registros.push({
      cbo: registro.cbo,
      dataAplicacao: registro.dataAplicacao,
      dataAprazamento: registro.dataAprazamento,
      dataRegistro: registro.dataRegistro,
      localDaAplicacao: registro.localAplicacao,
      nomeFabricante: registro.fabricante,
      estrategia: registro.estrategia,
      nomeProfissional: registro.profissional,
      lote: registro.lote,
      observacoes: registro.descricao,
      viaDeAdministracao: registro.viaAdministracao,
      isGestante: registro.isGestante,
      isPuerpera: registro.isPuerpera,
      isViajante: registro.isViajante,
      dataValidadeLote: registro.dataValidadeLote,
      unidadeSaude: registro.unidadeSaude,
    } as RegistroDoseProps)
  })

  return vacinacaoItem
}
